import json2csv from 'json2csv'
import csv from 'csvtojson'
import * as XLSX from 'xlsx'
import FileSaver from 'file-saver'
import { cloneDeep, without } from 'lodash'
import { Capacitor } from '@capacitor/core'

import { Format } from 'services/Dates'
import hasAccessToThis from 'services/utilFunctions/hasAccessToThis'
import getRoutes, { extractFavorites } from 'services/utilFunctions/getRoutes'
import { getAttendanceReasonName, getAttendanceReasonNames } from 'services/utilFunctions/getAttendanceReasonName'
import { getAttributeName, getAttributeNames, getAttributeRankName, getAttributeType } from 'services/utilFunctions/getAttributeName'
import { getBreakName, getBreakNames } from 'services/utilFunctions/getBreakName'
import { getConfigName, getConfigNames } from 'services/utilFunctions/getConfigName'
import { getDepartmentName, getDepartmentNames } from 'services/utilFunctions/getDepartmentName'
import { getFilterName, getFilterNames } from 'services/utilFunctions/getFilterName'
import { getHiringDataName, getHiringDataNames } from 'services/utilFunctions/getHiringDataName'
import { getPermissionName, getPermissionNames } from 'services/utilFunctions/getPermissionName'
import { getPositionName, getPositionNames } from 'services/utilFunctions/getPositionName'
import { getProcedureName, getProcedureNames } from 'services/utilFunctions/getProcedureName'
import { getProjectCode, getProjectName, getProjectNames } from 'services/utilFunctions/getProjectName'
import { getPunchRuleName, getPunchRuleNames } from 'services/utilFunctions/getPunchRuleName'
import { getReportsToPositions, getReportsToProjects, getReportsToTeams } from 'services/utilFunctions/getReportsToUser'
import { getRequestTypeName, getRequestTypeNames } from 'services/utilFunctions/getRequestTypeName'
import { getScheduleTemplateName, getScheduleTemplateNames } from 'services/utilFunctions/getScheduleTemplateName'
import { getSortName, getSortNames } from 'services/utilFunctions/getSortName'
import { getTagName, getTagNames, getTagCode } from 'services/utilFunctions/getTagName'
import { getTeamName, getTeamNames } from 'services/utilFunctions/getTeamName'
import { getUserName, getUserNames } from 'services/utilFunctions/getUserName'
import { getFiltersForSearch } from './utilFunctions/getFiltersForSearch'
import { getWagePremiumName, getWagePremiumNames, getWagePremiumEarningCode } from 'services/utilFunctions/getWagePremiumName'
import parseSettingInfo from 'services/utilFunctions/parseSettingInfo'
import { encoder as smsEncoder, segmenter as smsSegmenter } from 'services/utilFunctions/smsValidation'
import { store } from 'store/reducers'
import { t, currentLang } from 'translations'
import onClickPunchCardActionRequiredAlert from 'services/onClickPunchCardActionRequiredAlert'
import onClickPunchCardOverlappingTimeOffAlert from 'services/onClickPunchCardOverlappingTimeOffAlert'
import onClickPunchCardOverlappingAttendanceAlert from 'services/onClickPunchCardOverlappingAttendanceAlert'
import { getHelpLink } from 'services/NavBar'

import handleMobileSaveFiles from 'services/utilFunctions/handleMobileAppSaveFiles'

/**
 * Function to return a hash table of the provided array, mapped with the provided key of each object (key defaults to _id)
 * @param  {[Array]} array Array to return the hash table of
 * @param  {[String]} key   Key of the hash to user (defaults to _id)
 * @param  {|String|} objKey Property to use for the object (defaults to using the whole object as is)
 * @return {[Object]}       hash table object
 */
function hashTable<Type>(array: Array<Type>, key = '_id', objKey?: string): Record<string, Type> {
  // If the array is not provided, return an empty object
  if (!array) return {}

  // If the array is not an array, throw an error
  if (!Array.isArray(array)) throw new Error('hashTable function needs an array.')

  // Produce the hash table
  return array.reduce((hashTable, base) => {
    hashTable[base[key]] = objKey ? base[objKey] : base
    return hashTable
  }, {})
}

/**
 * Format phonenumber as: c (xxx) xxx-xxxx, handles only USA/Canada numbers
 * @param {number | string} number
 */
const parsePhone = (number: string): string => {
  if (!number) return ''
  number = String(number).replace('+', '')

  // Will return formattedNumber.
  // If phonenumber isn't longer than an area code, just show number
  let formattedNumber = number

  // if the first character is '1', strip it out and add it back
  const c = number[0] === '1' ? '1 ' : ''
  number = number[0] === '1' ? number.slice(1) : number

  // # (###) ###-#### as c (area) front-end
  const area = number.substring(0, 3)
  const front = number.substring(3, 6)
  const end = number.substring(6, 10)

  if (front) formattedNumber = `+${c}(${area}) ${front}`
  if (end) formattedNumber += `-${end}`

  return formattedNumber
}

const parseShiftInfoWithoutTranslation = ({ start, end, positionId, isExtraShift }: Partial<MData.Event>): string => {
  const outFormat = positionId ? 'humanDateTimeFullShift' : 'humanDateFull'
  const startFormatted = start && Format(start, { outFormat })
  const endFormatted = end && positionId && !isExtraShift ? ' - ' + Format(end, { outFormat: 'time' }) : ''
  const positionFormatted = positionId ? getPositionName(positionId) : ''

  return `${startFormatted}${endFormatted} ${positionFormatted}`
}

/**
 * Function that returns true if the date strings or objects passed are partial dates
 * @param { string | Date } start
 * @param { string | Date } end
 */
const isPartialDate = (start: string | Date, end: string | Date): boolean => {
  return Format(start, { outFormat: 'timeFull' }) !== '00:00:00' && Format(end, { outFormat: 'timeFull' }) !== '23:59:59'
}

/**
 * Will determine which type of attendance is provided (absence, lateness, early out) and will set it into the shift
 * @param {Shift} shift
 * @param {Attendance} attendance
 */

type Attendance = {
  requestTypeId: 1 | 2 | 3
  hasAbsence?: boolean
  hasEarlyOut?: boolean
  hasLateArrival?: boolean
}

type EventWithAttendanceType = Event & { hasAbsence: boolean; hasEarlyOut?: boolean; hasLateArrival?: boolean }

/**
 * Will determine which type of attendance is provided (absence, lateness, early out) and will set it into the shift
 * @param {Shift} shift
 * @param {Attendance} attendance
 */
function setAttendanceType(shift: Attendance | EventWithAttendanceType, requestTypeId: Attendance['requestTypeId']): Attendance | EventWithAttendanceType {
  switch (requestTypeId) {
    case 1:
      shift.hasAbsence = true
      break
    case 2:
      shift.hasLateArrival = true
      break
    case 3:
      shift.hasEarlyOut = true
      break
    default:
      break
  }

  return shift
}

const removeDiacritics = (str: string): string => {
  return typeof str === 'string' ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : str
}

/**
 *
 * @param {array} data
 * @param {array} fields array of {value, label}
 * @param {string} fileName
 * @param {object} options
 */
type GetCSV = <T>(
  data: T[],
  fields: ({ label: string } | string)[],
  fileName: string,
  options?: { delimiter?: string; quote?: string; header?: boolean; defaultValue?: string; noSeparator?: boolean }
) => string

const getCSV: GetCSV = (data, fields, fileName, options = {}) => {
  // Adds the .csv extension if it's missing
  if (!fileName.includes('.csv')) fileName += '.csv'
  // Get the settings
  const { settings } = store.getState() as MStore.RootState
  // Get the user's preferred csv delimiter
  const csvDelimiter = settings['csvDelimiter'] || ','
  // Remove diacritics (accents and stuff) from all rows
  data.map(row => Object.keys(row as Record<string, unknown>).map(key => (row[key] = removeDiacritics(row[key]))))

  // Build the csv
  const result = json2csv.parse(data, {
    fields: fields.map(field => {
      // Handle if field is an object
      if (typeof field === 'object') {
        return { ...field, label: removeDiacritics(field.label || '') }
      } else return removeDiacritics(field)
    }),
    delimiter: csvDelimiter,
    ...options
  })

  const type = 'text/csv'

  // Creates a blob to save
  const blob = new Blob([options.noSeparator ? result : `sep=${csvDelimiter}\n${result}`], { type })

  // Saves the csv file using the FileSaver module
  return Capacitor.isNativePlatform() ? handleMobileSaveFiles(result, fileName, type) : FileSaver.saveAs(blob, fileName)
}

const getXLSX = <T>(content: Array<T>, sheetName: string, fileName: string): void => {
  const wb: XLSX.WorkBook = XLSX.utils.book_new()
  const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(content)

  XLSX.utils.book_append_sheet(wb, ws, sheetName)

  return XLSX.writeFile(wb, fileName)
}

const secondsToTime = (value: string): string => {
  const sec_num = parseInt(value, 10) // don't forget the second param
  let hours: number | string = Math.floor(sec_num / 3600)
  let minutes: number | string = Math.floor((sec_num - hours * 3600) / 60)
  let seconds: number | string = sec_num - hours * 3600 - minutes * 60

  if (hours < 10) hours = '0' + hours
  if (minutes < 10) minutes = '0' + minutes
  if (seconds < 10) seconds = '0' + seconds

  return hours + ':' + minutes + ':' + seconds
}

const hoursToMinutes = (value: unknown): number => {
  if (typeof value == 'number') return value * 60

  return 0
}

const minutesToHours = (value: unknown): number => {
  if (typeof value == 'number') return value / 60

  return 0
}

const sortByTranslation = (a: string, b: string): number => {
  const x = removeDiacritics(t(a)).toLowerCase()
  const y = removeDiacritics(t(b)).toLowerCase()
  if (x < y) return -1
  if (x > y) return 1
  return 0
}

const zodiaqueSign = (date?: string): string => {
  try {
    if (date && date !== 'Invalid date') {
      const newDate = date.split('-')
      const day = parseInt(newDate[2].split('T')[0])
      const month = parseInt(newDate[1])
      // returns the zodiac sign according to day and month ( http://coursesweb.net/ )
      const zodiac = [
        '',
        'Capricorn',
        'Aquarius',
        'Pisces',
        'Aries',
        'Taurus',
        'Gemini',
        'Cancer',
        'Leo',
        'Virgo',
        'Libra',
        'Scorpio',
        'Sagittarius',
        'Capricorn'
      ]
      const lastDay = [19, 18, 20, 20, 21, 21, 22, 22, 21, 22, 21, 20, 19]
      return day > lastDay[month] ? zodiac[month * 1 + 1] : zodiac[month]
    } else {
      return 'unknown'
    }
  } catch (err) {
    return 'unknown'
  }
}

const readFile = async (fileName: Blob): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    try {
      // An instance of FileReader from the browser API (https://developer.mozilla.org/en-US/docs/Web/API/FileReader)
      const fileReader = new FileReader()

      fileReader.onload = () => {
        resolve(fileReader.result)
      }

      fileReader.readAsText(fileName, 'UTF-8')
    } catch (err) {
      const error = err as Error

      reject({ success: false, message: error.message })
    }
  })
}

const validateCSV = async (fileContents: string, expectedHeaders: string[] = []): Promise<unknown> => {
  return new Promise(resolve => {
    csv({ delimiter: [',', ';'] })
      .fromString(fileContents)
      .on('error', err => resolve({ success: false, message: err.message }))
      .on('header', headers => {
        // Validates that all mandatory headers are present in the file
        if (expectedHeaders.length) {
          const missingHeaders = without(expectedHeaders, ...headers)
          if (missingHeaders.length) resolve({ success: false, message: 'missingHeaders', missingHeaders })
        }
      })
      .then(rows =>
        resolve({
          success: true,
          result: rows.map(line => {
            // Removes the empty fields
            Object.keys(line).forEach(key => {
              if (line[key] === '') delete line[key]
            })
            return line
          })
        })
      )
  })
}

const validateCSVfromFile = async (fileName: Blob, expectedHeaders: string[]): Promise<unknown> => {
  const fileContents = await readFile(fileName)

  if (typeof fileContents === 'string') {
    return await validateCSV(fileContents, expectedHeaders)
  }
}

// Returns if the platform is windows or mac
const getPlatformKey = (): 'MAC' | 'WINDOWS' => (navigator.platform.indexOf('Mac') > -1 ? 'MAC' : 'WINDOWS')

type PlatformsOs = 'Android' | 'BlackBerry' | 'iOS' | 'Opera' | 'Windows' | 'any'

const isMobile = (platform: PlatformsOs = 'any'): RegExpMatchArray | null => {
  const platforms: Record<PlatformsOs, () => RegExpMatchArray | null> = {
    Android: function () {
      return navigator.userAgent.match(/Android/i)
    },
    BlackBerry: function () {
      return navigator.userAgent.match(/BlackBerry/i)
    },
    iOS: function () {
      return navigator.userAgent.match(/iPhone|iPad|iPod/i)
    },
    Opera: function () {
      return navigator.userAgent.match(/Opera Mini/i)
    },
    Windows: function () {
      return navigator.userAgent.match(/IEMobile/i)
    },
    any: function () {
      return platforms.Android() || platforms.BlackBerry() || platforms.iOS() || platforms.Opera() || platforms.Windows()
    }
  }

  return platforms[platform]()
}

/* intersperse: Return an array with the separator interspersed between
 * each element of the input array.
 *
 * > _([1,2,3]).intersperse(0)
 * [1,0,2,0,3]
 *
 * https://stackoverflow.com/a/23619085
 */
const intersperse = (arr: (string | number)[], sep: string | number = ', '): (string | number)[] => {
  if (arr.length === 0) {
    return []
  }

  return arr.slice(1).reduce(
    function (xs, x) {
      return xs.concat([sep, x])
    },
    [arr[0]]
  )
}

const isProd = process.env.NODE_ENV === 'production' && !window.Cypress

type Option = { isHidden?: boolean; label: string; alt?: string; options?: Option[] } & Record<string, unknown>

/* getOptions: Filter an array of options, limiting to render 20 when short input is provided */
const filterOptions = async (input: string, options: Option[], limit = 20): Promise<Option[]> => {
  const textIncludes = (label, input) => removeDiacritics(label.toLowerCase()).includes(removeDiacritics(input.toLowerCase()))

  const recursiveFilter = (options: Option[], searchText: string) => {
    const filteredOptions: Option[] = []
    for (const option of options) {
      if (Array.isArray(option.options)) {
        filteredOptions.push({ ...option, options: recursiveFilter(option.options, searchText) })
      } else {
        if (option.isHidden || !option.label) continue
        if (!textIncludes(`${option.label} ${option.alt ?? ''}`, searchText)) continue
        filteredOptions.push({ ...option })
      }
    }
    return filteredOptions
  }

  const matchingOptions = recursiveFilter(options, input)
  const limitedInput = input.length < 3 ? matchingOptions.splice(0, limit) : matchingOptions

  return limitedInput
}

/**
 * Function to extract various translations from a multi-line string
 */
type TranslationObject = { '#en': string[]; '#fr': string[] }
const availableLanguages = ['#fr', '#en']
const extractTranslation = (input = '', { extractCurrentLanguage = false } = {}): string | TranslationObject => {
  try {
    const translations = {} as TranslationObject
    let language = ''

    // Go to a new line ("\n") if there is none after or before the #en or #fr
    for (language of availableLanguages) {
      if (input.includes(language) && !input.includes('\n' + language)) {
        const index = input.indexOf(language)
        if (input.indexOf(language) !== 0) {
          const string = input.split('')
          string.splice(index, 0, '\n')
          input = string.join('')
        }
      }
      if (input.includes(language) && !input.includes(language + '\n')) {
        const index = input.indexOf(language)
        const string = input.split('')
        string.splice(index + 3, 0, '\n')
        input = string.join('')
      }
    }

    for (let line of input.split('\n')) {
      if (typeof line !== 'string') continue
      line = line.trim()
      if (availableLanguages.includes(line)) {
        language = line.slice(1)
        if (typeof translations[language] === 'undefined') translations[language] = []
        continue
      }

      if (language) translations[language].push(line)
    }

    const detectedTranslations = Object.keys(translations)
    if (!detectedTranslations.length) return input

    // this option makes this return the current language's translation
    if (extractCurrentLanguage) {
      let lang = currentLang()
      if (!detectedTranslations.includes(lang)) lang = detectedTranslations[0]
      return translations[lang].join('\n')
    }
    return translations
  } catch (err) {
    return input
  }
}

/**
 * A function that compacts a list of objects by summing certain fields together based
 * on a set of fields to check for uniqueness.
 * @param {array} objects an array of objects to sum
 * @param {array} matchKeys keys to use to determine uniqueness of objects
 * @param {array} sumKeys keys to sum together
 * @returns an array of objects
 */

const sumByFields = <T>(objects: Array<T> = [], sumKeys: Array<keyof T> = [], matchKeys: Array<keyof T> = []): T[] => {
  // Builds a key based on matchKeys to match different objects together
  const getMatchKey = object => matchKeys.map(key => object[key]).join(':')

  const objectMap = {}

  for (const object of objects) {
    const matchKey = getMatchKey(object)

    if (!objectMap[matchKey]) {
      objectMap[matchKey] = cloneDeep(object)
    } else {
      for (const sumKey of sumKeys) objectMap[matchKey][sumKey] += object[sumKey] || 0
    }
  }

  return Object.values(objectMap)
}

type FlattenObject = (obj: unknown, previousKeys: string[], newObj: Record<string, unknown>) => void

const flattenObject: FlattenObject = (obj, previousKeys = [], newObj = {}): unknown | never => {
  if (typeof obj === 'object') {
    for (const key in obj) {
      const value = obj[key]
      const keys = [...previousKeys, key]
      if (value.constructor !== Object) newObj[keys.join('.')] = value
      else flattenObject(value, keys, newObj)
    }
    return newObj
  }

  throw new Error('Object must be an object')
}

const getS3KeyFromUrl = (url: string): string => {
  const key = url.split('amazonaws.com/')[1]
  return key
}

export {
  extractFavorites,
  extractTranslation,
  filterOptions,
  flattenObject,
  getAttendanceReasonName,
  getAttendanceReasonNames,
  getAttributeName,
  getAttributeNames,
  getAttributeRankName,
  getAttributeType,
  getBreakName,
  getBreakNames,
  getConfigName,
  getConfigNames,
  getCSV,
  getDepartmentName,
  getDepartmentNames,
  getFilterName,
  getFilterNames,
  getFiltersForSearch,
  getHelpLink,
  getHiringDataName,
  getHiringDataNames,
  getPermissionName,
  getPermissionNames,
  getPlatformKey,
  getPositionName,
  getPositionNames,
  getProcedureName,
  getProcedureNames,
  getProjectCode,
  getProjectName,
  getProjectNames,
  getPunchRuleName,
  getPunchRuleNames,
  getReportsToPositions,
  getReportsToProjects,
  getReportsToTeams,
  getRequestTypeName,
  getRequestTypeNames,
  getRoutes,
  getS3KeyFromUrl,
  getScheduleTemplateName,
  getScheduleTemplateNames,
  getSortName,
  getSortNames,
  getTagCode,
  getTagName,
  getTagNames,
  getTeamName,
  getTeamNames,
  getUserName,
  getUserNames,
  getWagePremiumEarningCode,
  getWagePremiumName,
  getWagePremiumNames,
  getXLSX,
  handleMobileSaveFiles,
  hasAccessToThis,
  hashTable,
  hoursToMinutes,
  intersperse,
  isMobile,
  isPartialDate,
  isProd,
  minutesToHours,
  onClickPunchCardActionRequiredAlert,
  onClickPunchCardOverlappingTimeOffAlert,
  onClickPunchCardOverlappingAttendanceAlert,
  parsePhone,
  parseSettingInfo,
  parseShiftInfoWithoutTranslation,
  readFile,
  removeDiacritics,
  secondsToTime,
  setAttendanceType,
  smsEncoder,
  smsSegmenter,
  sortByTranslation,
  sumByFields,
  validateCSV,
  validateCSVfromFile,
  zodiaqueSign
}
